import { cn, type InputProps } from '@repo/ui';
import { Eye, EyeOff } from 'lucide-react';
import { type ReactElement, useId, useState } from 'react';

interface TextFieldProps extends Omit<InputProps, 'id' | 'hasError'> {
  readonly label: string;
  /** 검증 실패 메시지. 값이 있으면 오류 테두리와 함께 입력 아래에 보인다. */
  readonly error?: string | undefined;
  readonly description?: string | undefined;
  readonly isRequired?: boolean;
  readonly containerClassName?: string;
}

/**
 * 연회색으로 채운 입력 필드 — 라벨은 위에, 포커스하면 흰 바탕과 흑연 테두리로 바뀐다.
 * type="password" 면 오른쪽에 입력 내용 보기/숨기기 버튼을 단다.
 * 그 버튼의 이름에는 라벨 글자를 넣지 않는다 — 라벨로 입력을 찾는 쪽(getByLabel)이 버튼까지 잡지 않게.
 * 라벨 · 설명 · 오류는 FormField 와 같은 방식(htmlFor · aria-describedby)으로 연결한다.
 */
export const TextField = ({
  label,
  error,
  description,
  isRequired = false,
  containerClassName,
  className,
  type,
  ...inputProps
}: TextFieldProps): ReactElement => {
  const id = useId();
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;
  const hasError = Boolean(error);
  const isPassword = type === 'password';
  const [isRevealed, setIsRevealed] = useState(false);

  const describedBy =
    [error ? errorId : null, description ? descriptionId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className={cn('flex flex-col gap-2', containerClassName)}>
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {isRequired ? (
          <span aria-hidden className="ml-0.5 text-muted-foreground">
            *
          </span>
        ) : null}
      </label>

      <div className="relative">
        <input
          id={id}
          type={isPassword && isRevealed ? 'text' : type}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-14 w-full rounded-none border bg-muted/60 px-4 text-[15px] text-foreground outline-none transition-colors',
            'placeholder:text-muted-foreground/70 hover:bg-muted',
            'focus:bg-background focus:hover:bg-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            hasError
              ? 'border-destructive bg-background'
              : 'border-transparent focus:border-foreground',
            isPassword && 'pr-12',
            className,
          )}
          {...inputProps}
        />
        {isPassword ? (
          <button
            type="button"
            aria-label={isRevealed ? '입력 내용 숨기기' : '입력 내용 보기'}
            aria-pressed={isRevealed}
            aria-controls={id}
            onClick={() => setIsRevealed((value) => !value)}
            className="absolute inset-y-0 right-0 flex w-12 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          >
            {isRevealed ? (
              <EyeOff aria-hidden strokeWidth={1.5} className="size-[18px]" />
            ) : (
              <Eye aria-hidden strokeWidth={1.5} className="size-[18px]" />
            )}
          </button>
        ) : null}
      </div>

      {description ? (
        <p id={descriptionId} className="text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
